import { useMemo, useState, useEffect, useContext } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
} from "@tanstack/react-table";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Typography,
} from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import DownloadIcon from "@mui/icons-material/Download";
import { EventContext } from "../../../contexts/EventContext";

const DataTable = ({
  data,
  fields,
  update,
  initialFilterValues = {},
  loadMore,
  initialPageSize = 20,
  initialPageIndex = 0,
  onPageSizeChange,
  onPageIndexChange,
  handleExport,
}) => {
  const { userData } = useContext(EventContext);
  const [sorting, setSorting] = useState([]);
  const [globalFilter, setGlobalFilter] = useState("");
  const [pagination, setPagination] = useState({
    pageIndex: initialPageIndex,
    pageSize: initialPageSize,
  });

  // State for filter dialog and filter values
  const [openFilterDialog, setOpenFilterDialog] = useState(false);
  const [filterValues, setFilterValues] = useState(initialFilterValues);
  const [openExportDialog, setOpenExportDialog] = useState(false);

  useEffect(() => {
    setFilterValues(initialFilterValues);
  }, [initialFilterValues]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: initialPageIndex }));
  }, [initialPageIndex]);

  // Inline style objects for table styling
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
  };
  const thStyle = {
    border: "1px solid #ddd",
    padding: "8px",
    backgroundColor: "#f2f2f2",
    textAlign: "left",
  };
  const tdStyle = {
    border: "1px solid #ddd",
    padding: "8px",
    textAlign: "left",
  };

  // Define your table columns dynamically based on the provided fields array.
  const columns = useMemo(() => {
    if (fields && fields.length > 0) {
      return fields.map((field) => ({
        accessorKey: field.key,
        header: field.name,
      }));
    } else if (data && data.length > 0) {
      return Object.keys(data[0]).map((key) => ({
        accessorKey: key,
        header: key.charAt(0).toUpperCase() + key.slice(1),
      }));
    } else {
      return [];
    }
  }, [fields, data]);

  // Create the table instance using TanStack Table hooks.
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
      globalFilter,
      pagination,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  // Handlers for the filter dialog
  const handleOpenFilterDialog = () => {
    setOpenFilterDialog(true);
  };

  const handleCloseFilterDialog = () => {
    setOpenFilterDialog(false);
  };

  const handleInputChange = (fieldKey, value, type, bound) => {
    setFilterValues((prev) => {
      const updated = { ...prev };
      if (type === "number") {
        // For number fields, store an object with potential gte and lte values
        const current = updated[fieldKey] || {};
        current[bound] = value;
        updated[fieldKey] = current;
      } else {
        updated[fieldKey] = value;
      }
      return updated;
    });
  };

  const handleClear = () => {
    setFilterValues({});
    update({});
  };

  const handleApplyFilters = () => {
    console.log("Applying filters:", filterValues);
    update(filterValues);
    setOpenFilterDialog(false);
  };

  // Handler for changing page size via dropdown
  const handlePageSizeChange = (event) => {
    const newSize = Number(event.target.value);
    setPagination((prev) => ({
      ...prev,
      pageSize: newSize,
      pageIndex: 0,
    }));
    // Pass the new size to the parent so it persists
    if (onPageSizeChange) {
      onPageSizeChange(newSize);
    }
  };

  return (
    <div style={{ padding: 20 }}>
      {/* Filter Controls */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <div style={{ display: "flex", gap: "20px", alignItems: "center" }}>
          <IconButton onClick={handleOpenFilterDialog}>
            <TuneIcon
              sx={{ color: userData?.org?.style?.primaryColor || "grey" }}
            />
          </IconButton>
          {Object.keys(filterValues).length > 0 ? (
            <Button variant="outlined" onClick={handleClear}>
              Reset
            </Button>
          ) : null}
        </div>
        <div style={{ marginLeft: "auto" }}>
          {data.length > 0 ? (
            <IconButton
              sx={{ marginRight: "20px" }}
              onClick={() => setOpenExportDialog(true)}
            >
              <DownloadIcon />
            </IconButton>
          ) : null}
          <FormControl>
            <InputLabel>Rows per page</InputLabel>
            <Select
              label="Rows per page"
              value={pagination.pageSize}
              onChange={handlePageSizeChange}
              style={{ width: "120px" }}
              size="small"
            >
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      {/* Table */}
      <table style={tableStyle}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  style={thStyle}
                  onClick={
                    header.column.getCanSort()
                      ? header.column.getToggleSortingHandler()
                      : undefined
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                  {{
                    asc: " 🔼",
                    desc: " 🔽",
                  }[header.column.getIsSorted()] ?? null}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id}>
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} style={tdStyle}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Pagination controls */}
      <div
        style={{
          marginTop: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          Page {table.getState().pagination.pageIndex + 1} of{" "}
          {table.getPageCount()}
        </div>
        <div>
          <Button
            variant="outlined"
            size="small"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            style={{ marginRight: "8px" }}
          >
            Previous
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={async () => {
              if (table.getCanNextPage()) {
                table.nextPage();
              } else if (loadMore) {
                await loadMore();
                setPagination((prev) => {
                  const newPageIndex = prev.pageIndex + 1;
                  if (onPageIndexChange) {
                    onPageIndexChange(newPageIndex);
                  }
                  return { ...prev, pageIndex: newPageIndex };
                });
              }
            }}
          >
            {table.getCanNextPage() ? "Next" : "Load next 100"}
          </Button>
        </div>
        <div>
          Showing {table.getPrePaginationRowModel().rows.length} results
        </div>
      </div>

      {/* Filter Dialog */}
      <Dialog open={openFilterDialog} onClose={handleCloseFilterDialog}>
        <DialogTitle>Filters</DialogTitle>
        <DialogContent>
          {fields.map((field) => (
            <div key={field.key} style={{ marginBottom: "16px" }}>
              {field.type === "text" ? (
                <TextField
                  label={field.name}
                  fullWidth
                  value={filterValues[field.key] || ""}
                  onChange={(e) =>
                    handleInputChange(field.key, e.target.value, field.type)
                  }
                />
              ) : field.type === "number" ? (
                <>
                  <TextField
                    label={`${field.name} (Min)`}
                    type="number"
                    fullWidth
                    value={
                      (filterValues[field.key] &&
                        filterValues[field.key].gte) ||
                      ""
                    }
                    onChange={(e) =>
                      handleInputChange(
                        field.key,
                        e.target.value,
                        field.type,
                        "gte"
                      )
                    }
                    style={{ marginBottom: "8px" }}
                  />
                  <TextField
                    label={`${field.name} (Max)`}
                    type="number"
                    fullWidth
                    value={
                      (filterValues[field.key] &&
                        filterValues[field.key].lte) ||
                      ""
                    }
                    onChange={(e) =>
                      handleInputChange(
                        field.key,
                        e.target.value,
                        field.type,
                        "lte"
                      )
                    }
                  />
                </>
              ) : field.type === "select" ? (
                <FormControl fullWidth>
                  <InputLabel>{field.name}</InputLabel>
                  <Select
                    label={field.name}
                    fullWidth
                    value={filterValues[field.key] || ""}
                    onChange={(e) =>
                      handleInputChange(field.key, e.target.value, field.type)
                    }
                    displayEmpty
                  >
                    <MenuItem value="" />

                    {field.values.map((val) => (
                      <MenuItem key={val} value={val}>
                        {val}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </div>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFilterDialog}>Cancel</Button>
          <Button onClick={handleApplyFilters}>Apply</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={openExportDialog}
        onClose={() => setOpenExportDialog(false)}
      >
        <DialogTitle>Export Data</DialogTitle>
        <DialogContent>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              padding: 30,
            }}
          >
            {Object.keys(filterValues).length > 0 ? (
              <Button
                variant="outlined"
                onClick={() => {
                  handleExport("filtered");
                  setOpenExportDialog(false);
                }}
              >
                export filtered data
              </Button>
            ) : null}

            <Button
              variant="outlined"
              onClick={() => {
                handleExport("all");
                setOpenExportDialog(false);
              }}
            >
              export everything
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenExportDialog(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default DataTable;
